import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageMeta, JsonLd } from '@/components/feature/Head';

const FORM_URL = 'https://readdy.ai/api/form/d9qqmufnahb7382ovdq0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');
    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt') {
          payload.append(key, value as string);
        }
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const faqs = [
    {
      q: '国产区精选内容聚合平台是什么？',
      a: '国产区是一个专注于国产原创内容聚合与分类导航的平台。我们汇聚影视精选、文学创作、音乐之声、视觉艺术、科技前沿、生活方式、历史文化和游戏动漫等八大领域的优质国产原创内容，为用户提供一站式内容发现和阅读体验。',
    },
    {
      q: '如何找到我感兴趣的内容？',
      a: '您可以通过首页的分类导航进入感兴趣的内容领域，也可以使用页面顶部的搜索功能查找特定主题的文章。每个分类页面都提供了该领域下的全部精选内容，方便您集中浏览。',
    },
    {
      q: '国产区的内容来源是什么？',
      a: '国产区的内容来自国产原创作者和内容创作者，所有内容都经过编辑团队的精心筛选和审核，确保质量和水准。我们致力于推广和传播优秀的本土原创内容。',
    },
    {
      q: '我可以在国产区发布内容吗？',
      a: '目前国产区正在逐步开放作者入驻功能。如果您是内容创作者，欢迎通过本页面的联系表单与我们沟通，我们会在功能开放后第一时间通知您。',
    },
    {
      q: '国产区平台是否收费？',
      a: '国产区平台对读者完全免费。我们致力于让更多人接触到优质的国产原创内容，目前不向读者收取任何费用。',
    },
  ];

  return (
    <>
      <PageMeta
        title="关于我们 - 国产区精选内容聚合平台"
        description="国产区精选内容聚合平台致力于汇聚优质国产原创内容，为用户提供全面的国产区资源导航。了解我们的品牌故事、平台理念和联系方式。"
        keywords="国产区,关于国产区,国产区平台,国产区品牌"
        canonical="https://guochanqu.com/about"
        ogTitle="关于我们 - 国产区精选内容聚合平台"
        ogDescription="国产区精选内容聚合平台致力于汇聚优质国产原创内容，了解我们的品牌故事和联系方式。"
        ogUrl="https://guochanqu.com/about"
        ogType="website"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于国产区精选内容聚合平台',
          description: '国产区精选内容聚合平台致力于汇聚优质国产原创内容。',
          url: 'https://guochanqu.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '国产区精选内容聚合平台',
            url: 'https://guochanqu.com',
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://guochanqu.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://guochanqu.com/about' },
          ],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a,
            },
          })),
        }}
      />

      <main className="pt-20 min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line mr-1"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 mb-4 inline-block">
                品牌介绍
              </span>
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-6">
                关于国产区精选内容聚合平台
              </h1>
              <div className="text-sm text-foreground-600 leading-relaxed space-y-4">
                <p>
                  国产区精选内容聚合平台创立于2024年，是一家专注于国产原创内容聚合与分类导航的综合内容平台。我们的使命是汇聚中国最优质的原创内容，为用户提供全面、便捷的内容发现和阅读体验。
                </p>
                <p>
                  在信息爆炸的时代，优质的本土原创内容往往被淹没在海量信息之中。国产区通过精准的内容分类体系和专业的编辑推荐机制，帮助用户快速找到真正有价值的国产原创内容，节省信息筛选的时间成本。
                </p>
                <p>
                  我们覆盖影视精选、文学创作、音乐之声、视觉艺术、科技前沿、生活方式、历史文化、游戏动漫等八大核心领域，每个领域都由专业编辑精心筛选和维护，确保内容的品质和深度。
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20warm%20natural%20lighting%2C%20Chinese%20cultural%20elements%2C%20wooden%20furniture%2C%20bookshelves%20with%20books%2C%20cozy%20atmosphere%2C%20content%20creation%20studio%2C%20professional%20yet%20warm%20aesthetic&width=800&height=600&seq=about-gcq-01&orientation=landscape"
                alt="国产区精选内容聚合平台 - 办公环境"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 mb-4 inline-block">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                他们眼中的国产区
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                来自用户和创作者的真实评价，是我们不断前行的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '张先生',
                  role: '资深读者',
                  comment: '国产区是我每天必看的内容平台，分类清晰，文章质量很高。推荐的内容总能带来惊喜，特别喜欢影视精选和文学创作这两个分类。',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '内容创作者',
                  comment: '作为一个原创作者，我非常喜欢国产区对原创内容的重视。这里的读者群体很有品味，互动也很积极，是一个非常好的创作分享平台。',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '在校研究生',
                  comment: '国产区的历史文化分类做得特别好，文章深度和专业性都很强。科技前沿的内容也帮我了解了很多行业动态，强烈推荐。',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-xl bg-background-50 flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4 flex-1">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 text-xs font-medium">
                      {review.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { value: '50万+', label: '月活跃用户' },
                { value: '5000+', label: '优质原创文章' },
                { value: '98%', label: '用户好评率' },
                { value: '2000+', label: '合作创作者' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center p-5 rounded-xl bg-background-50">
                  <p className="text-2xl md:text-3xl font-heading text-primary-600 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 mb-4 inline-block">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                与国产区取得联系
              </h2>
              <p className="text-sm text-foreground-600">
                有任何问题或建议，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@guochanqu.com' },
                { icon: 'ri-map-pin-line', title: '办公地址', value: '中国北京市朝阳区' },
                { icon: 'ri-time-line', title: '工作时间', value: '周一至周五 9:00-18:00' },
              ].map((info, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${info.icon} text-lg`}></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">{info.title}</p>
                  <p className="text-sm text-foreground-800">{info.value}</p>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              action={FORM_URL}
              method="POST"
              className="bg-background-100 rounded-xl p-6 md:p-8"
              data-readdy-form="true"
            >
              <h3 className="text-lg font-heading text-foreground-950 mb-6">发送留言</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="about-name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="about-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="about-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="about-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="about-message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="about-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="company_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  <i className="ri-check-line mr-1.5"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full sm:w-auto px-8 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    发送中...
                  </>
                ) : (
                  '发送留言'
                )}
              </button>
            </form>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于国产区的常见疑问，这里都有答案
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-background-50 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}